import { Fragment, useState, type ReactNode } from 'react';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import InfoSection from '@/components/Approval/@components/InfoSection';
import EyeIcon from '@/public/icons/approval/eye.svg';
import type { SaVoucherItem, SaVoucherLine } from '@/types/approval';
import {
  formatPostingDate,
  getVoucherCompanyName,
  openSapVoucherLink,
  SA_VOUCHER_ITEM_COLUMNS,
} from '@/utils/approval/saVoucher';
import {
  SaVoucherCompany,
  SaVoucherDetailCell,
  SaVoucherDetailTable,
  SaVoucherHeaderRow,
  SaVoucherLinkButton,
  SaVoucherTableRoot,
  SaVoucherTableScroll,
} from './styles';

type SaVoucherInfoProps = {
  vouchers: SaVoucherLine[];
  zSum: string | null;
  /** 레거시 문서처럼 라인에 회사명이 없을 때 */
  companyName?: string | null;
  title?: ReactNode;
};

const COLUMN_COUNT = 13;

const VoucherDetail = ({ items }: { items: SaVoucherItem[] }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <SaVoucherDetailCell colSpan={COLUMN_COUNT}>
        <SaVoucherDetailTable>
          <thead>
            <tr>
              {SA_VOUCHER_ITEM_COLUMNS.map(({ key, label }) => (
                <th key={key}>{t(label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item, index) => (
                <tr key={index}>
                  {SA_VOUCHER_ITEM_COLUMNS.map(({ key, align }) => (
                    <td key={key} data-align={align}>
                      {item[key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={SA_VOUCHER_ITEM_COLUMNS.length}>
                  {t('상세 정보가 없습니다.')}
                </td>
              </tr>
            )}
          </tbody>
        </SaVoucherDetailTable>
      </SaVoucherDetailCell>
    </tr>
  );
};

/**
 * SA 전표(99) 전표 라인 표 — 작성 필드·조회 슬롯 공용. 열 구성은 레거시 폼 그대로.
 * 상세(G/L) 행은 레거시처럼 버튼 하나로 전 전표를 함께 펼침/접음, 처음엔 접힘. 상세 없는 전표는 안내 문구
 */
const SaVoucherInfo = ({
  vouchers,
  zSum,
  companyName,
  title,
}: SaVoucherInfoProps) => {
  const { t } = useTranslation();
  const company = getVoucherCompanyName(vouchers) ?? companyName;
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  return (
    <InfoSection title={title}>
      <SaVoucherHeaderRow>
        <SaVoucherCompany>
          {company && `${t('회사명')} : ${company}`}
        </SaVoucherCompany>
        <BoxButton
          variant="primary"
          size="small"
          aria-expanded={isDetailOpen}
          onClick={() => setIsDetailOpen((open) => !open)}
        >
          {isDetailOpen ? t('전표 상세 내역 접기') : t('전표 상세 내역 보기')}
        </BoxButton>
      </SaVoucherHeaderRow>
      <SaVoucherTableScroll>
        <SaVoucherTableRoot>
          <thead>
            <tr>
              <th>{t('순번')}</th>
              <th>{t('전표유형')}</th>
              <th>{t('전기일')}</th>
              <th>{t('전표번호')}</th>
              <th>{t('통화')}</th>
              <th>{t('입력금액')}</th>
              <th>{t('원화금액')}</th>
              <th>{t('적요')}</th>
              <th>{t('사용일자')}</th>
              <th>{t('사용처')}</th>
              <th>{t('사용처주소')}</th>
              <th>{t('라인적요')}</th>
              <th>{t('보기')}</th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map((v, index) => (
              <Fragment key={`${v.seq ?? index}-${v.documentNumber ?? ''}`}>
                <tr>
                  <td>{v.seq ?? index + 1}</td>
                  <td>{v.documentType}</td>
                  <td>{formatPostingDate(v.postingDate)}</td>
                  <td>{v.documentNumber}</td>
                  <td>{v.currency}</td>
                  <td data-align="right">{v.amount}</td>
                  <td data-align="right">{v.localAmount}</td>
                  <td data-align="left">{v.headerText}</td>
                  <td>{v.text1}</td>
                  <td>{v.text2}</td>
                  <td data-align="left">{v.text3}</td>
                  <td data-align="left">{v.itemText}</td>
                  <td>
                    {v.link && (
                      <SaVoucherLinkButton
                        type="button"
                        aria-label={t('SAP 전표 보기')}
                        onClick={() => openSapVoucherLink(v.link)}
                      >
                        <EyeIcon width={16} height={16} />
                      </SaVoucherLinkButton>
                    )}
                  </td>
                </tr>
                {isDetailOpen && <VoucherDetail items={v.items ?? []} />}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={4}>{t('합계')}</th>
              <td colSpan={COLUMN_COUNT - 4}>{zSum}</td>
            </tr>
          </tfoot>
        </SaVoucherTableRoot>
      </SaVoucherTableScroll>
    </InfoSection>
  );
};

/** 전표 없음·조회 실패·로딩 안내 — 표 자리에 문구만 */
export const SaVoucherNotice = ({
  title,
  message,
  isError,
}: {
  title?: ReactNode;
  message: string;
  isError?: boolean;
}) => (
  <InfoSection title={title}>
    <ApprovalNoticeBox role={isError ? 'alert' : 'status'}>
      {message}
    </ApprovalNoticeBox>
  </InfoSection>
);

export default SaVoucherInfo;
